import styled from 'styled-components';

export const ScrollContainer = styled.div`
	height: 100%;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
`;

export const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 40px 20px 120px 20px;
	width: 100%;
	text-align: center;
`;

export const PlanReview = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	max-width: 840px;
	margin: 0 auto;
	width: 100%;
	text-align: left;
`;

export const PlanCard = styled.div`
	padding: 24px;
	border-radius: 12px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	background-color: ${({ theme }) => theme.gray[0]};
`;

export const CardTitle = styled.h3`
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 8px 0;
	color: ${({ theme }) => theme.gray[900]};
`;

export const CardDescription = styled.p`
	font-size: 14px;
	line-height: 1.5;
	margin: 0 0 16px 0;
	color: ${({ theme }) => theme.gray[600]};
`;

export const PlanRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: 16px;
	width: 100%;
`;

export const PlanLabel = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: ${(p) => p.theme.gray[700]};
	width: 120px;
	flex-shrink: 0;
	padding-top: 8px;
	text-transform: uppercase;
	letter-spacing: 0.05em;
`;

export const PlanValue = styled.div`
	flex: 1;
	min-width: 0;
`;

export const ButtonRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 20px;
	width: 100%;
`;

export const SecondaryButton = styled.button`
	background: transparent;
	border: 1px solid ${(p) => p.theme.gray[200]};
	color: ${(p) => p.theme.gray[1000]};
	padding: 8px 16px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	&:hover {
		background: ${(p) => p.theme.gray[100]};
	}
`;

export const PrimaryButton = styled.button`
	background: ${(p) => p.theme.blue[500]};
	border: none;
	color: ${(p) => p.theme.gray[900]};
	padding: 8px 20px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	&:hover {
		background: ${(p) => p.theme.blue[600]};
	}
	&:disabled {
		opacity: 0.5;
	}
`;

export const TextArea = styled.textarea`
	box-sizing: border-box;
	width: 100%;
	min-height: 180px;
	padding: 10px 12px;
	border-radius: 6px;
	border: 1px solid ${(p) => p.theme.gray[200]};
	background: ${(p) => p.theme.gray[0]};
	color: ${(p) => p.theme.gray[1000]};
	font-family: inherit;
	font-size: 14px;
	resize: vertical;
	&:focus {
		outline: none;
		border-color: ${(p) => p.theme.blue[500]};
	}
`;

export const Input = styled.input`
	box-sizing: border-box;
	width: 100%;
	padding: 10px 12px;
	border-radius: 6px;
	border: 1px solid ${(p) => p.theme.gray[200]};
	background: ${(p) => p.theme.gray[0]};
	color: ${(p) => p.theme.gray[1000]};
	font-family: inherit;
	font-size: 14px;
	&:focus {
		outline: none;
		border-color: ${(p) => p.theme.blue[500]};
	}
`;

export const TagList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	padding: 8px;
	border: 1px solid ${(p) => p.theme.gray[200]};
	border-radius: 6px;
	background: ${(p) => p.theme.gray[0]};
`;

export const Tag = styled.div`
	background: ${(p) => p.theme.gray[100]};
	color: ${(p) => p.theme.gray[1000]};
	padding: 4px 10px;
	border-radius: 4px;
	font-size: 12px;
	display: flex;
	align-items: center;
	gap: 6px;
`;

export const RemoveTag = styled.button`
	background: none;
	border: none;
	padding: 0;
	outline: none;
	color: ${(p) => p.theme.gray[400]};
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		color: ${(p) => p.theme.gray[1000]};
	}
`;

export const AddTagInput = styled.input`
	border: none;
	background: transparent;
	font-size: 12px;
	outline: none;
	color: ${(p) => p.theme.gray[1000]};
	min-width: 100px;
	flex: 1;
`;

interface DropZoneCardProps {
	isDragging?: boolean;
}

export const DropZoneCard = styled.div<DropZoneCardProps>`
	position: relative;
	padding: 24px;
	border-radius: 12px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	background-color: ${({ theme }) => theme.gray[0]};
	transition: all 0.2s ease;
`;

export const DragOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${({ theme }) => theme.blue[300]}AA;
	border: 2px dashed ${({ theme }) => theme.blue[500]};
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	z-index: 1000;
	transition: opacity 0.2s ease-in-out;
`;

export const DragOverlayContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`;

export const DragOverlayText = styled.div`
	color: ${({ theme }) => theme.gray[1000]};
	font-size: 14px;
	font-weight: 500;
`;

export const AttachmentList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	align-items: center;
`;

export const AddAttachmentButton = styled.button`
	height: 32px;
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 4px 12px;
	border: 1px dashed ${(p) => p.theme.gray[400]};
	border-radius: 12px;
	background: transparent;
	color: ${(p) => p.theme.gray[600]};
	font-size: 12px;
	font-weight: 500;
	&:hover {
		border-color: ${(p) => p.theme.blue[500]};
		color: ${(p) => p.theme.blue[500]};
		background: ${(p) => p.theme.gray[100]};
	}
`;
